import React, { useMemo } from 'react';
import { Repeat, Package, PackageOpen, PackageX, ArrowRight, RotateCcw } from 'lucide-react';

/**
 * Diagramme SVG animé qui visualise le trajet (aller seul ou aller-retour).
 * Se met à jour en temps réel depuis les champs departure/arrival/distance.
 */
function TripDiagram({ departure, arrival, distance, tripType, returnMode }) {
  const isRoundTrip = tripType === 'round_trip';
  const totalKm = isRoundTrip && distance ? distance * 2 : distance;

  const pinA = (
    <g>
      <circle cx="50" cy="60" r="18" fill="#1db584" />
      <text x="50" y="66" textAnchor="middle" fill="white" fontSize="18" fontWeight="700">A</text>
    </g>
  );
  const pinB = (
    <g>
      <circle cx="350" cy="60" r="18" fill={isRoundTrip ? '#7c3aed' : '#1db584'} />
      <text x="350" y="66" textAnchor="middle" fill="white" fontSize="18" fontWeight="700">B</text>
    </g>
  );

  const returnStrokeColor = returnMode === 'empty' ? '#d1d5db' : '#7c3aed';
  const returnOpacity = returnMode === 'empty' ? 0.5 : 1;

  return (
    <svg
      viewBox="0 0 400 130"
      className="w-full h-auto"
      preserveAspectRatio="xMidYMid meet"
      style={{ maxHeight: '120px' }}
      aria-label="Trajectoire de l'opération"
    >
      <defs>
        <marker id="arrow-out" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="8" markerHeight="8" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill="#1db584" />
        </marker>
        <marker id="arrow-ret" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="8" markerHeight="8" orient="auto">
          <path d="M 0 0 L 10 5 L 0 10 z" fill={returnStrokeColor} />
        </marker>
      </defs>

      {/* Aller — courbe supérieure */}
      <path
        d="M 68 54 C 150 18, 250 18, 332 54"
        stroke="#1db584"
        strokeWidth="2.5"
        fill="none"
        markerEnd="url(#arrow-out)"
        strokeLinecap="round"
      >
        <animate attributeName="stroke-dasharray" from="0,500" to="500,0" dur="1.2s" fill="freeze" />
      </path>

      {/* Retour — courbe inférieure (visible uniquement si round_trip) */}
      {isRoundTrip && (
        <path
          d="M 332 66 C 250 102, 150 102, 68 66"
          stroke={returnStrokeColor}
          strokeWidth="2.5"
          fill="none"
          strokeDasharray={returnMode === 'empty' ? '6 4' : 'none'}
          opacity={returnOpacity}
          markerEnd="url(#arrow-ret)"
          strokeLinecap="round"
        >
          <animate attributeName="stroke-dashoffset" from="500" to="0" dur="1.4s" fill="freeze" />
        </path>
      )}

      {/* Villes au-dessous des pins — tronquées pour tenir */}
      <text x="50" y="108" textAnchor="middle" fill="#1f2937" fontSize="11" fontWeight="600">
        <title>{departure || 'Départ'}</title>
        {(departure || 'Départ').split(',')[0].slice(0, 18)}
      </text>
      <text x="350" y="108" textAnchor="middle" fill="#1f2937" fontSize="11" fontWeight="600">
        <title>{arrival || 'Arrivée'}</title>
        {(arrival || 'Arrivée').split(',')[0].slice(0, 18)}
      </text>

      {/* Distance totale au centre */}
      {totalKm > 0 && (
        <g>
          <rect x="160" y="46" width="80" height="24" rx="12" fill="white" stroke="#e5e7eb" />
          <text x="200" y="62" textAnchor="middle" fill="#374151" fontSize="12" fontWeight="600">
            {Math.round(totalKm)} km
          </text>
        </g>
      )}

      {pinA}
      {pinB}
    </svg>
  );
}

/**
 * Section complète Aller-retour : chips segmentés + radio 3 options + champs conditionnels.
 */
export default function RoundTripSection({ formData, setFormData, merchandiseTypes, containerTypes }) {
  const isRoundTrip = formData.tripType === 'round_trip';
  const returnMode = formData.returnCargoMode;
  const distance = formData.distance_km || 0;

  const returnCargoOptions = useMemo(() => ([
    { id: 'same', label: 'Même que l\'aller', icon: Package, desc: 'Le camion reste chargé avec la même marchandise' },
    { id: 'different', label: 'Marchandise différente', icon: PackageOpen, desc: 'Nouveau chargement au point B' },
    { id: 'empty', label: 'Retour à vide', icon: PackageX, desc: 'Aucune valorisation du retour' },
  ]), []);

  const setTripType = (v) => {
    setFormData(prev => ({
      ...prev,
      tripType: v,
      // Reset des champs retour si on repasse en one-way
      ...(v === 'one_way' ? {
        returnCargoMode: null,
        returnMerchandiseType: '',
        returnWeight: '',
        returnContainerType: '',
      } : {
        returnCargoMode: prev.returnCargoMode || 'same',
      }),
    }));
  };

  const setReturnMode = (mode) => {
    setFormData(prev => ({
      ...prev,
      returnCargoMode: mode,
      ...(mode === 'same' || mode === 'empty' ? {
        returnMerchandiseType: mode === 'empty' ? '' : prev.merchandiseType,
        returnMerchandiseTypeOther: mode === 'empty' ? '' : (prev.merchandiseTypeOther || ''),
        returnWeight: mode === 'empty' ? 0 : prev.weight,
        returnContainerType: mode === 'empty' ? '' : prev.containerType,
      } : {}),
    }));
  };

  // Reprendre toutes les spécifications de l'aller sur le retour (1-clic)
  const copyOutboundToReturn = () => {
    setFormData(prev => ({
      ...prev,
      returnMerchandiseType: prev.merchandiseType || '',
      returnMerchandiseTypeOther: prev.merchandiseTypeOther || '',
      returnWeight: prev.weight || '',
      returnContainerType: prev.containerType || '',
    }));
  };

  // Déterminer si l'opération nécessite un conteneur (parité avec le form principal)
  const isContainerOperation = formData.operationType === 'transport_conteneur';

  return (
    <div className="space-y-4" data-testid="round-trip-section">
      {/* Chips segmentés — Apple style */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Type de trajet
        </label>
        <div
          role="radiogroup"
          aria-label="Type de trajet"
          className="inline-flex items-center p-1 bg-gray-100 rounded-full"
        >
          <button
            type="button"
            role="radio"
            aria-checked={!isRoundTrip}
            onClick={() => setTripType('one_way')}
            data-testid="trip-type-one_way"
            className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm transition-all ${
              !isRoundTrip
                ? 'bg-white shadow-sm text-gray-900 font-semibold'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <ArrowRight className="w-3.5 h-3.5" />
            Aller simple
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={isRoundTrip}
            onClick={() => setTripType('round_trip')}
            data-testid="trip-type-round_trip"
            className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm transition-all ${
              isRoundTrip
                ? 'bg-white shadow-sm text-purple-700 font-semibold'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Aller-retour
          </button>
        </div>
      </div>

      {/* Mini-diagramme SVG — toujours visible, se met à jour en live */}
      {(formData.departure || formData.arrival) && (
        <div
          className={`rounded-lg border px-3 py-2 transition-colors max-w-xl mx-auto ${
            isRoundTrip ? 'border-purple-200 bg-purple-50/30' : 'border-gray-200 bg-gray-50'
          }`}
          data-testid="trip-diagram"
        >
          <TripDiagram
            departure={formData.departure}
            arrival={formData.arrival}
            distance={distance}
            tripType={formData.tripType}
            returnMode={returnMode}
          />
          {isRoundTrip && distance > 0 && (
            <p className="text-[11px] text-center text-gray-600 mt-0.5">
              <Repeat className="inline w-3 h-3 mr-1 text-purple-600" />
              Distance totale : <strong>{Math.round(distance * 2)} km</strong> ({Math.round(distance)} × 2)
            </p>
          )}
        </div>
      )}

      {/* Bloc retour — uniquement si round-trip */}
      {isRoundTrip && (
        <div
          className="rounded-lg border border-purple-200 bg-white p-4 space-y-3"
          data-testid="return-block"
        >
          <div className="flex items-center gap-2">
            <RotateCcw className="w-4 h-4 text-purple-600" />
            <h4 className="text-sm font-semibold text-purple-900">
              Trajet retour {formData.arrival && formData.departure ? `${formData.arrival} → ${formData.departure}` : ''}
            </h4>
          </div>

          {/* Radio 3 options */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {returnCargoOptions.map(opt => {
              const Icon = opt.icon;
              const active = returnMode === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setReturnMode(opt.id)}
                  data-testid={`return-mode-${opt.id}`}
                  className={`text-left rounded-lg border p-3 transition-all ${
                    active
                      ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-500'
                      : 'border-gray-200 hover:border-purple-300 bg-white'
                  }`}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon className={`w-4 h-4 ${active ? 'text-purple-600' : 'text-gray-500'}`} />
                    <span className={`text-sm font-medium ${active ? 'text-purple-900' : 'text-gray-700'}`}>
                      {opt.label}
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-500 leading-tight">{opt.desc}</p>
                </button>
              );
            })}
          </div>

          {/* Champs spécifiques — uniquement si "différente" */}
          {returnMode === 'different' && (
            <div className="space-y-3 pt-2 border-t border-dashed border-purple-200">
              {/* Bouton "Reprendre les spec. de l'aller" en 1 clic */}
              <button
                type="button"
                onClick={copyOutboundToReturn}
                data-testid="copy-outbound-to-return-btn"
                className="inline-flex items-center gap-1.5 text-xs text-purple-700 hover:text-purple-900 hover:bg-purple-100 rounded-md px-2 py-1 transition-colors"
                title="Pré-remplit la marchandise, le conteneur et le poids depuis l'aller"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Reprendre les spec. de l&apos;aller
              </button>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Si opération conteneur : sélecteur conteneur retour, sinon : marchandise */}
                {isContainerOperation ? (
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Type de conteneur au retour *
                    </label>
                    <select
                      value={formData.returnContainerType || ''}
                      onChange={(e) => setFormData(p => ({ ...p, returnContainerType: e.target.value }))}
                      data-testid="return-container-select"
                      className="w-full h-9 px-2 text-sm rounded-md border border-gray-300 bg-white"
                    >
                      <option value="">Sélectionner un conteneur...</option>
                      {(containerTypes || []).map(c => (
                        <option key={c.value} value={c.value}>
                          {c.icon} {c.label} — {c.dimensions}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Marchandise au retour *
                    </label>
                    <select
                      value={formData.returnMerchandiseType || ''}
                      onChange={(e) => setFormData(p => ({ ...p, returnMerchandiseType: e.target.value }))}
                      data-testid="return-merchandise-select"
                      className="w-full h-9 px-2 text-sm rounded-md border border-gray-300 bg-white"
                    >
                      <option value="">Sélectionner...</option>
                      {(merchandiseTypes || []).map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Poids retour (tonnes) *
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="220"
                    step="0.5"
                    value={formData.returnWeight || ''}
                    onChange={(e) => setFormData(p => ({ ...p, returnWeight: parseFloat(e.target.value) || 0 }))}
                    data-testid="return-weight-input"
                    className="w-full h-9 px-2 text-sm rounded-md border border-gray-300"
                  />
                </div>
              </div>

              {/* Champ "Précisez..." quand l'user choisit "Autre" pour la marchandise retour */}
              {!isContainerOperation && formData.returnMerchandiseType === 'autre' && (
                <div className="animate-in slide-in-from-top-2 duration-300">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Précisez la marchandise au retour *
                  </label>
                  <input
                    type="text"
                    value={formData.returnMerchandiseTypeOther || ''}
                    onChange={(e) => setFormData(p => ({ ...p, returnMerchandiseTypeOther: e.target.value }))}
                    data-testid="return-merchandise-other-input"
                    placeholder="Ex: Produits pharmaceutiques sensibles..."
                    className="w-full h-9 px-2 text-sm rounded-md border border-gray-300"
                  />
                </div>
              )}
            </div>
          )}

          {/* Badge d'alerte si retour à vide */}
          {returnMode === 'empty' && (
            <div className="flex items-start gap-2 p-2 rounded-md bg-amber-50 border border-amber-200">
              <PackageX className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-800">
                <strong>Retour à vide.</strong> Envisagez de proposer ce trajet à un 2ᵉ client
                pour optimiser votre marge (gain potentiel sur {Math.round(distance)} km).
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
